import type { LoaderFunctionArgs } from '@remix-run/node'
import { Link, NavLink, Outlet, redirect, useLoaderData, useLocation, useNavigate } from '@remix-run/react'
import { requireUser } from '#app/modules/auth/auth.server'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import {
  ArrowLeftEndOnRectangleIcon,
  Bars3Icon,
  BellAlertIcon,
  ChatBubbleBottomCenterTextIcon,
  ClipboardIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  HomeIcon,
  HomeModernIcon,
  InboxIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserCircleIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Fragment, useEffect, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '#convex/_generated/api.js'
import AgencyAccountLabel from '#app/components/AgencyAccountLabel'
import { getSession, commitSession } from '#app/modules/auth/auth-session.server'
import { convexHttpClient } from '#app/utils/convexContext.js'
import { UnreadBadge, UnreadMessage } from '#app/components/UnreadBadge'
import ViewAsSelector from '#app/components/lists/ViewAsSelector.js'
import { Doc, Id } from '#convex/_generated/dataModel.js'
import SubdivisionName from '#app/components/SubdivisionName.js'
import { StrandedUnreadBadge } from '#app/components/StrandedUnreadBadge.js'
import RegionLabel from '#app/components/RegionLabel.js'
import NeighborhoodLabel from '#app/components/NeighborhoodLabel.js'
import SubdivisionSelector from '#app/components/lists/SubdivisionSelector.js'

export const ROUTE_PATH = '/dashboard' as const

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request)
  const session = await getSession(request.headers.get("Cookie"))
  let agencyAccountId = session.get("agencyAccountId")
  let region = session.get("region")
  const pendingApprovals = await convexHttpClient.query(api.message.getPendingApprovalForUser, { userId: user._id })
  
  if(pendingApprovals) {
    return redirect("/pending-approval")
  }
  if (agencyAccountId == null) {
    const adminAgencyAccounts = await convexHttpClient.query(api.agencyAccount.getAdminAgencyAccounts, { userId: user._id })
    if (adminAgencyAccounts && adminAgencyAccounts.length > 0) {
      agencyAccountId = adminAgencyAccounts[0]
      session.set("agencyAccountId", agencyAccountId)
      const agencyAccount = await convexHttpClient.query(api.agencyAccount.getById, { id: agencyAccountId })
      if(agencyAccount && agencyAccount.regions && agencyAccount.regions.length > 0) {
        session.set("region", agencyAccount.regions[0])
      }
      return redirect("/dashboard", {
        headers: {
          "Set-Cookie": await commitSession(session),
        },
      })
    } else if (user.subdivisions && user.subdivisions.length > 0) {
      let agencyAccount = await convexHttpClient.query(api.subdivision.getSubdivisionAgencyAccount, { subdivisionId: user.subdivisions[0] })
      if(agencyAccount) {
        agencyAccountId = agencyAccount._id
        session.set("agencyAccountId", agencyAccountId)
      }
      let region = await convexHttpClient.query(api.region.getRegionForSubdivision, { subdivisionId: user.subdivisions[0] })
      if(region) {
        session.set("region", region._id)
      } else {
        session.set('region', "jd7bg922ej1fstw3ebwcdf3gd574de6t")
      }
      return redirect("/dashboard", {
        headers: {
        "Set-Cookie": await commitSession(session),
        },
      })
    } else {
      agencyAccountId = ""
      session.set("agencyAccountId", agencyAccountId)
      return redirect("/new-user", {
        headers: {
          "Set-Cookie": await commitSession(session),
        },
      })
    }
  }
  // let region = session.get('region');
  let subdivision = session.get('subdivision');
  if(region == null || subdivision == null) {
    if(user.subdivisions && user.subdivisions.length > 0) {
      // CHECK IF USER SHOULD BE IN SUBDIVISION
      session.set('subdivision', user.subdivisions[0])
      subdivision = user.subdivisions[0]
      const regionForSubdivision = await convexHttpClient.query(api.region.getRegionForSubdivision, { subdivisionId: user.subdivisions[0] })
      if(regionForSubdivision) {
        session.set('region', regionForSubdivision._id)
        region = regionForSubdivision._id
      } else {
        console.log("REGION NOT FOUND FOR SUBDIVISION", user.subdivisions[0])
        session.set('region', "jd7bg922ej1fstw3ebwcdf3gd574de6t")
        region = null
      }
      return redirect("/dashboard", {
        headers: {
        "Set-Cookie": await commitSession(session),
        },
      })
    } else {
      session.set('region', "jd7bg922ej1fstw3ebwcdf3gd574de6t")
    }
  }
  let superUser = false
  let resident = false

  if(user.superUser == true) {
    superUser = true
  }
  let board: Doc<"Subdivision">[] = []
  if(user.properties && user.properties.length > 0 && user.subdivisions && user.subdivisions.length > 0) {
    resident = true
    for(const subdivisionId of user.subdivisions) {
      const subdivisionObject = await convexHttpClient.query(api.subdivision.getById, { id: subdivisionId })
      if(subdivisionObject && subdivisionObject.boardMembers && subdivisionObject.boardMembers.length > 0) {
        if(subdivisionObject.boardMembers.includes(user._id)) {
          board.push(subdivisionObject)
        }
      }
    }
  }
  await commitSession(session)
  let finalRegionCheck = session.get('region')
  if(finalRegionCheck == null) {
    return redirect('/logout')
  }
  return  { user, superUser, resident, agencyAccountId, region, subdivision, board }
}

let navigation = [
  { name: 'Dashboard', href: '', icon: RectangleGroupIcon },
  { name: 'Inbox', href: 'inbox', icon: InboxIcon },
  { name: 'Reminders', href: 'reminders', icon: BellAlertIcon },
  { name: 'Activations', href: 'activations', icon: ShieldCheckIcon },
  // { name: 'Communications', href: 'communications', icon: ChatBubbleBottomCenterTextIcon },
  { name: 'Bookkeeping', href: 'books', icon: CurrencyDollarIcon },
]

let requests = [
  // { name: 'Payments', href: 'payments', icon: CurrencyDollarIcon },
  { name: 'Dashboard', href: '', icon: RectangleGroupIcon },
  { name: 'Payments', href: 'payments', icon: CurrencyDollarIcon },
  { name: 'Maintenance', href: 'maintenance', icon: ClipboardIcon },
  { name: 'Architecture Review', href: 'arc-reviews', icon: HomeModernIcon },
  { name: 'Violations', href: 'violations', icon: ExclamationTriangleIcon },
  { name: 'Governing Documents', href: 'covenants', icon: HomeIcon },
  { name: 'Files', href: 'files', icon: DocumentIcon   },
  // { name: 'Settings', href: 'settings', icon: CogIcon },
]

const settings = [
  { name: 'Account', href: 'myaccount', icon: UserCircleIcon },
  // { name: 'Notifications', href: 'notifications', icon: BellAlertIcon },
]

const adminSettings = [
  { name: 'Neighborhoods', href: 'neighborhoods', icon: HomeIcon },
  { name: 'Vendors', href: 'vendors', icon: TruckIcon },
  // { name: 'Admin', href: 'admin', icon: CogIcon },
]

const superAdminSettings = [
  // { name: 'New Setup', href: 'setup', icon: RectangleGroupIcon },
  { name: 'Super Admin', href: 'supersecretadminpage', icon: GlobeAltIcon },
  { name: 'Pending Users', href: 'stranded-users', icon: UserPlusIcon },
  // { name: 'Stripe Issues', href: 'stripe-issues', icon: CurrencyDollarIcon },
  { name: 'Timed Payments', href: 'timed-payments', icon: ClockIcon },
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export default function Dashboard() {
  const { user, superUser, resident, agencyAccountId, region, subdivision, board } = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  if(region == null) {
    navigate('/logout')
  }
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation();
  const agencyAdmin = useQuery(api.agencyAccount.isUserAdminOfAgencyAccount, { userId: user._id, agencyAccountId: agencyAccountId });
  const regionAdmin = useQuery(api.region.userAdminRegions, { userId: user._id });
  const subdivisionAdmin = useQuery(api.subdivision.userAdminSubdivisions, { userId: user._id });
  const userSubdivision = subdivision ? useQuery(api.subdivision.getById, { id: subdivision }) : null;
  const agencyaccount = useQuery(api.agencyAccount.getById, { id: agencyAccountId });
  const unreads = useQuery(api.message.getUnreadMessagesCount, {userId: user._id, regionId: region, board: board.map(b => b._id), subdivision: subdivision != undefined ? subdivision : null})
  const unreadsActivations = useQuery(api.message.getUnreadActivationssCount, {userId: user._id, regionId: region, board: board.map(b => b._id), subdivision: subdivision})
  const residentUnreads = useQuery(api.message.getResidentUnreadInboxCount, {userId: user._id})
  const [selectedUser, setSelectedUser] = useState<Doc<"User"> | Doc<"Subdivision"> | null>(null)
  const mySubdivisions = useQuery(api.user.getUserSubdivisions, { userId: user._id })
  const reminders = useQuery(api.reminder.getRegionReminderUnreads, { regionId: region, board: board.map(b => b._id), subdivision: subdivision })

  if(unreads == null || mySubdivisions == null || residentUnreads == null) {
    return <div>Loading...</div>
  }

  return (
    <>
      <div>
        <Transition show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <TransitionChild
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div className="fixed inset-0 flex">
              <TransitionChild
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <TransitionChild
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-black" aria-hidden="true" />
                      </button>
                    </div>
                  </TransitionChild>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2 border-r-2 pt-6">
                    <SidebarContent
                      user={user}
                      superUser={superUser}
                      agencyAdmin={agencyAdmin}
                      regionAdmin={regionAdmin}
                      subdivisionAdmin={subdivisionAdmin}
                      agencyaccount={agencyaccount}
                      region={region}
                      resident={resident}
                      subdivision={subdivision}
                      board={board}
                      mySubdivisions={mySubdivisions}
                      userSubdivision={userSubdivision ?? null}
                      unreads={unreads}
                      unreadsActivations={unreadsActivations}
                      unreadsReminders={reminders}
                      residentUnreads={residentUnreads}
                      selectedUser={selectedUser}
                      setSelectedUser={setSelectedUser}
                      location={location}
                    />
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col overflow-y-auto border-r border-gray bg-white px-6">
            <SidebarContent
              user={user}
              superUser={superUser}
              agencyAdmin={agencyAdmin}
              regionAdmin={regionAdmin}
              subdivisionAdmin={subdivisionAdmin}
              agencyaccount={agencyaccount}
              region={region}
              resident={resident}
              subdivision={subdivision}
              board={board}
              mySubdivisions={mySubdivisions}
              userSubdivision={userSubdivision ?? null}
              unreads={unreads}
              unreadsActivations={unreadsActivations}
              unreadsReminders={reminders}
              residentUnreads={residentUnreads}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              location={location}
            />
          </div>
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
          <button type="button" className="-m-2.5 p-2.5 text-gray lg:hidden" onClick={() => setSidebarOpen(true)}>
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <b className="text-2xl text-blue">NeighborCloud</b>
          <Link to="#">
            <span className="sr-only">Your profile</span>
          </Link>
        </div>
      </div>
      <div className="min-h-screen max-h-screen">
        <Outlet />
      </div>
    </>
  )
}

function SidebarContent({ 
  user, 
  superUser, 
  agencyAdmin, 
  regionAdmin, 
  subdivisionAdmin, 
  agencyaccount, 
  region, 
  resident,
  subdivision,
  board,
  mySubdivisions,
  userSubdivision,
  unreads,
  unreadsActivations,
  unreadsReminders,
  residentUnreads,
  selectedUser,
  setSelectedUser,
  location
}: {
  user: Doc<"User">,
  superUser: boolean,
  agencyAdmin: boolean | null | undefined,
  regionAdmin: (Doc<"Region"> | null)[] | undefined,
  subdivisionAdmin: (Doc<"Subdivision"> | null)[] | undefined,
  agencyaccount: Doc<"AgencyAccount"> | null | undefined,
  region: Id<"Region">,
  resident: boolean,
  subdivision: Id<"Subdivision">,
  board: Doc<"Subdivision">[],
  mySubdivisions: Doc<"Subdivision">[],
  userSubdivision: Doc<"Subdivision"> | null,
  unreads: UnreadMessage[] | undefined,
  unreadsActivations: UnreadMessage[] | undefined,
  unreadsReminders: UnreadMessage[] | undefined,
  residentUnreads: UnreadMessage[] | undefined,
  selectedUser: Doc<"User"> | Doc<"Subdivision"> | null,
  setSelectedUser: (user: Doc<"User"> | Doc<"Subdivision"> | null) => void,
  location: any
}) {
  const [filteredRequests, setFilteredRequests] = useState(requests);
  const [filteredNavigation, setFilteredNavigation] = useState(navigation);
  const [userId, setUserId] = useState<Id<"User"> | null>(null)
  const [selectedSubdivision, setSelectedSubdivision] = useState<Doc<"Subdivision"> | null>(null)
  const [selectedSubdivisionId, setSelectedSubdivisionId] = useState<Id<"Subdivision"> | null>(null)

  useEffect(() => {
    // Reset to original arrays
    let newRequests = [...requests];
    let newNavigation = [...navigation];

    // Filter based on board
    if (board) {
      newRequests = newRequests.filter((request) => request.name !== 'Dashboard');
    }

    // Filter based on service level
    if (userSubdivision?.serviceLevel === 'Bookkeeping Only') {
      newRequests = newRequests.filter(
        (request) => 
          request.name !== 'Architecture Review' && 
          request.name !== 'Violations' && 
          request.name !== 'Maintenance'
      );
      newNavigation = newNavigation.filter(
        (item) => item.name !== 'Inbox' && item.name !== 'Reminders'
      );
    }

    setFilteredRequests(newRequests);
    setFilteredNavigation(newNavigation);
  }, [userSubdivision, board]);

  useEffect(() => {
    if(!location.pathname.includes('/view') || location.pathname.includes('/view-as')) {
      setSelectedUser(null)
      setUserId(null)
    }
    if(location.pathname.includes('/view/') && selectedUser == null) {
      const userId = location.pathname.split('/')[3]
      const subdivisionId = location.pathname.split('/')[4]
      setUserId(userId)
      setSelectedSubdivisionId(subdivisionId)
    }
  }, [location])
  
  function getViewAsLink(team: {href: string}) {
    if(location.pathname.includes('/view') && !location.pathname.includes('/view-as')) {
      const linkRoot = 'view'
      const userId = location.pathname.split('/')[3]
      const subdivision = location.pathname.split('/')[4]
      return linkRoot + '/' + userId + '/' + subdivision + '/' + team.href
    } else if(selectedUser && 'name' in selectedUser) {
      return `admin/${selectedUser._id}/${team.href}`
    }
    return team.href
  }
  
  function getRequests() {
    if(selectedUser && 'name' in selectedUser) {
      return filteredRequests.filter((request) => request.href !== 'payments')
    }
    return filteredRequests
  }

  function isLinkActive(isActive: boolean, href: string) {
    if(location.pathname.includes('/neighborhoods') && href === 'vendors') {
      return false
    }
    if(href === '' && location.pathname !== '/dashboard') {
      return false
    }

    return isActive || location.pathname.includes(href)
  }

  function isRequestActive(isActive: boolean, href: string) {
    if(href === 'dashboard' && location.pathname.endsWith('dashboard')) {
      return true
    }
    
    return (isActive || location.pathname.includes(href)) && href != 'dashboard'
  }

  function showAdminSettings() {
    if(superUser || agencyAdmin) {
      return true
    }
    if(regionAdmin && regionAdmin.length > 0 && regionAdmin.filter((r) => r != null).map((r) => r._id).includes(region)) {
      return true
    }
    if(subdivisionAdmin && subdivisionAdmin.length > 0 && subdivisionAdmin.filter((s) => s != null).map((s) => s._id).includes(subdivision)) {
      return true
    }
    if(board && board.length > 0 && board.filter((b) => b != null).map((b) => b._id).includes(subdivision)) {
      return true
    }
    return false
  }

  return (
    <>
      <div className="flex h-16 shrink-0 items-center">
        <b className="text-2xl text-blue">NeighborCloud</b>
      </div>
      {(superUser || agencyAdmin) && agencyaccount ? 
        <AgencyAccountLabel agencyaccount={agencyaccount} region={region} user={user} /> :
        regionAdmin && regionAdmin.length > 0 ?
          <RegionLabel regions={regionAdmin.filter((r) => r != null)} currentRegion={region} /> :
          subdivisionAdmin && subdivisionAdmin.length > 0 ?
            <p>{subdivisionAdmin[0]?.name}</p> :
            null
      }
      {resident && !superUser && (
        <NeighborhoodLabel 
          subdivisions={mySubdivisions.filter(s => s != null) as Doc<"Subdivision">[]} 
          subdivision={subdivision} />
      )}
      <nav className="flex flex-1 flex-col">
        
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          {showAdminSettings() && (
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                {filteredNavigation.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      className={({isActive}) => {
                        return classNames(
                          isLinkActive(isActive, item.href)
                          ? 'bg-blue text-white'
                          : 'text-booBlack hover:text-blue hover:bg-offWhite',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                        )}}
                        >
                      {({isActive}) => (
                        <div className="flex flex-row flex-1 justify-between">
                          <div className="flex flex-row justify-start">
                            <item.icon
                              className={classNames(
                                isLinkActive(isActive, item.href) ? 'text-white' : 'text-darkBlue group-hover:text-blue',
                                'h-6 w-6 shrink-0 mr-3'
                              )}
                              aria-hidden="true"
                              />
                            {item.name}
                          </div>
                          {unreads && unreads.length > 0 && (
                            <UnreadBadge type={item.href} unreads={unreads}/>
                          )}
                          {unreadsActivations && unreadsActivations.length > 0 && (
                            <UnreadBadge type={item.href} unreads={unreadsActivations}/>
                          )}
                          {unreadsReminders && unreadsReminders.length > 0 && (
                            <UnreadBadge type={item.href} unreads={unreadsReminders}/>
                          )}
                        </div>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
            )}
          <li>
            {subdivisionAdmin && subdivisionAdmin.length > 0 ?
                <p>
                  <SubdivisionName subdivision={subdivisionAdmin[0]!._id} string={true} />
                </p> :
                null
            }
            { (superUser || agencyAdmin || (regionAdmin && regionAdmin.length > 0)) && region && (
              <>
                <p>VIEW AS</p>
                {selectedSubdivision == null ?
                  <SubdivisionSelector selectedSubdivision={selectedSubdivision} setSelectedSubdivision={setSelectedSubdivision} regionId={region} subdivisionId={selectedSubdivisionId} /> :
                  <>
                    <p className="font-bold uppercase justify-between flex flex-row">
                      <SubdivisionName subdivision={selectedSubdivision._id} string={true} /> 
                      <XMarkIcon className="h-5 w-5 text-blue" onClick={() =>  {
                        setSelectedSubdivision(null)
                        setSelectedSubdivisionId(null)
                        setSelectedUser(null)
                        setUserId(null)
                      }}/>
                      </p>
                    <ViewAsSelector subdivisionId={selectedSubdivision._id} selectedUser={selectedUser} setSelectedUser={setSelectedUser} userId={userId} />
                  </>
                }
              </>
            )}
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {selectedUser && getRequests().map((team) => (
                <li key={team.name}>
                  <NavLink
                    to={getViewAsLink(team)}
                    className={({isActive}) => {
                      return classNames(
                        isLinkActive(isActive, team.href)
                        ? 'bg-blue text-white'
                        : 'text-booBlack hover:text-blue hover:bg-offWhite',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                    )}}
                  >
                    {({isActive}) => (
                      <>
                        <team.icon
                          className={classNames(
                            isLinkActive(isActive, team.href) ? 'text-white' : 'text-darkBlue group-hover:text-blue',
                            'h-6 w-6 shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {team.name}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}

              {board.length > 0 && <div className="text-xs font-medium leading-6 text-blue ml-2">My Property</div>}
              {resident && requests.map((team) => {
                let link = team.href
                if(team.name == 'Governing Documents') {
                  link = 'covenants/' + subdivision
                }
                if(team.name == 'Files') {
                  link = 'files/' + subdivision + '/financials'
                }
                return<li key={team.name}>
                  <NavLink
                    to={link}
                    className={({isActive}) => {
                      return classNames(
                        isLinkActive(isActive, team.href)
                        ? 'bg-blue text-white'
                        : 'text-booBlack hover:text-blue hover:bg-offWhite',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                    )}}
                  >
                    {({isActive}) => (
                      <div className="flex flex-row flex-1 justify-between">
                          <div className="flex flex-row justify-start">
                            <team.icon
                              className={classNames(
                                isLinkActive(isActive, team.href) ? 'text-white' : 'text-darkBlue group-hover:text-blue',
                                'h-6 w-6 shrink-0 mr-3'
                              )}
                              aria-hidden="true"
                            />
                            {team.name}
                          </div>
                          {residentUnreads && residentUnreads.length > 0 && (
                            <UnreadBadge type={team.href} unreads={residentUnreads}/>
                          )}
                      </div>
                    )}
                  </NavLink>
                </li>
              })}
              </ul>
              </li>
          <li>
            <div className="text-xs font-medium leading-6 text-blue">Settings</div>
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {settings.map((team) => (
                <li key={team.name}>
                  <NavLink
                    to={team.href}
                    className={({isActive}) => {
                      return classNames(
                      isActive || location.pathname.includes(team.href)
                      ? 'bg-blue text-white'
                      : 'text-booBlack hover:text-blue hover:bg-offWhite',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                    )}}
                  >
                    {({isActive}) => (
                      <>
                        <team.icon
                          className={classNames(
                            isActive || location.pathname.includes(team.href) ? 'text-white' : 'text-darkBlue group-hover:text-blue',
                            'h-6 w-6 shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {team.name}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
              {(agencyAdmin || superUser || (regionAdmin && regionAdmin.length > 0)) && adminSettings.map((team) => (
                <li key={team.name}>
                  <NavLink
                    to={team.href}
                    className={({isActive}) => {
                      return classNames(
                      isLinkActive(isActive, team.href)
                      ? 'bg-blue text-white'
                      : 'text-booBlack hover:text-blue hover:bg-offWhite',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                    )}}
                  >
                    {({isActive}) => (
                      <>
                        <team.icon
                          className={classNames(
                            isLinkActive(isActive, team.href) ? 'text-white' : 'text-darkBlue group-hover:text-blue',
                            'h-6 w-6 shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {team.name}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
              {user && user.superUser && user.superUser == true && (
                superAdminSettings.map((team) => (
                  <li key={team.name}>
                    <NavLink
                      to={team.href}
                      className={({isActive}) => {
                        return classNames(
                          isLinkActive(isActive, team.href)
                          ? 'bg-blue text-white'
                          : 'text-gray hover:text-blue hover:bg-offWhite',
                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium'
                      )}}
                    >
                      {({isActive}) => (
                        <div className="flex flex-row flex-1 justify-between">
                          <div className="flex flex-row justify-start">
                            <team.icon
                              className={classNames(
                                isLinkActive(isActive, team.href) ? 'text-white' : 'text-gray group-hover:text-blue',
                                'h-6 w-6 shrink-0 mr-3'
                              )}
                              aria-hidden="true"
                            />
                            {team.name}
                          </div>
                          {team.name == 'Pending Users' && <StrandedUnreadBadge />}
                        </div>
                      )}
                    </NavLink>
                  </li>
                ))
              )}
            </ul>
          </li>
          <li className="mt-auto mb-6">
            {/* <Link 
              to="https://warp-skirt-9f6.notion.site/Knowledge-Base-2c6e30d8e14c4a46bd792d6ddd63b93f?pvs=4" 
              target="_blank"
            >
              <div className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium text-blue -mx-2 hover:bg-offWhite">
                <LifebuoyIcon 
                  className="h-6 w-6 text-blue" 
                  aria-hidden="true"
                />
                Help
              </div>
            </Link> */}
            <Link 
              to="/auth/logout" 
            >
              <div className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium text-blue -mx-2 hover:bg-offWhite">
                <ArrowLeftEndOnRectangleIcon 
                  className="h-6 w-6 text-blue" 
                  aria-hidden="true"
                />
                Logout
              </div>
            </Link>
          </li>
        </ul>
      </nav>
    </>
  )
}

